import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { Building2, Home, Package, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { fetchStockByIds } from '@/services/inventarioInsumos.service.js';
import { alistarEntrega } from '@/services/entregasInsumos.service.js';

const AlistarEntregaModal = ({ isOpen, onClose, solicitud, onSuccess }) => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [observaciones, setObservaciones] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingStock, setIsLoadingStock] = useState(false);

  // Cargar stock disponible para cada item
  useEffect(() => {
    if (isOpen && solicitud) {
      loadStockDisponible();
    }
  }, [isOpen, solicitud]);

  const loadStockDisponible = async () => {
    setIsLoadingStock(true);
    try {
      // Obtener todos los IDs de los items
      const consumibleIds = solicitud.items.map(item => item.id);
      
      // Hacer una sola llamada para obtener el stock de todos los items
      const response = await fetchStockByIds(consumibleIds);
      const stockMap = response?.data || {};

      // Mapear los items con su stock correspondiente
      const itemsConStock = solicitud.items.map(item => {
        const stock = stockMap[item.id];
        const stockDisponible = stock?.cantidad_disponible || 0;

        return {
          ...item,
          stock_disponible: stockDisponible,
          cantidad_alistar: Math.min(item.cantidad, stockDisponible),
          tiene_stock: stockDisponible >= item.cantidad
        };
      });

      setItems(itemsConStock);
    } catch (error) {
      console.error('Error al cargar stock:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar el stock disponible',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingStock(false);
    }
  };

  const handleCantidadChange = (index, value) => {
    const newItems = [...items];
    const cantidad = parseInt(value) || 0;
    const maxCantidad = Math.min(newItems[index].cantidad, newItems[index].stock_disponible);
    
    newItems[index].cantidad_alistar = Math.max(0, Math.min(cantidad, maxCantidad));
    setItems(newItems);
  };

  const handleAlistar = async () => {
    // Validar que haya items para alistar
    const itemsAlistar = items.filter(item => item.cantidad_alistar > 0);
    
    if (itemsAlistar.length === 0) {
      toast({
        title: 'Error',
        description: 'Debe alistar al menos un item con cantidad mayor a 0',
        variant: 'destructive'
      });
      return;
    }

    // Validar que los items tengan stock disponible
    const itemsSinStock = itemsAlistar.filter(item => item.stock_disponible === 0);
    if (itemsSinStock.length > 0) {
      toast({
        title: 'Error',
        description: `Los siguientes items no tienen stock: ${itemsSinStock.map(i => i.nombre).join(', ')}`,
        variant: 'destructive'
      });
      return;
    }

    // Validar stock insuficiente
    const itemsStockInsuficiente = itemsAlistar.filter(item => item.cantidad_alistar > item.stock_disponible);
    if (itemsStockInsuficiente.length > 0) {
      toast({
        title: 'Error',
        description: `Stock insuficiente para: ${itemsStockInsuficiente.map(i => i.nombre).join(', ')}`,
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);
    try {
      // Obtener usuario actual (deberías tenerlo en un contexto)
      const usuario = {
        id: localStorage.getItem('userId') || '68ee96b27238402c10e314ce',
        nombre: localStorage.getItem('userName') || 'Usuario'
      };

      const data = {
        solicitud_id: solicitud._id,
        alistador: usuario,
        items: itemsAlistar.map(item => ({
          id: item.id,
          nombre: item.nombre,
          cantidad: item.cantidad_alistar,
          tipo: item.tipo,
          observacion: item.observacion || ''
        })),
        observaciones: observaciones
      };

      const response = await alistarEntrega(data);

      toast({
        title: 'Éxito',
        description: 'Entrega alistada correctamente',
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error al alistar entrega:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo alistar la entrega',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!solicitud) return null;

  const totalAlistar = items.reduce((sum, item) => sum + (item.cantidad_alistar || 0), 0);
  const todosConStock = items.every(item => item.tiene_stock);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            <Package className="h-6 w-6 text-blue-600" />
            Alistar Entrega
          </DialogTitle>
          <DialogDescription>
            Verificar stock y confirmar cantidades a alistar
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Información de la Solicitud */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-blue-600" />
              <div>
                <div className="text-xs text-gray-500">Edificio</div>
                <div className="font-medium">{solicitud.edificio?.nombre}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Home className="h-4 w-4 text-green-600" />
              <div>
                <div className="text-xs text-gray-500">Apto/casa</div>
                <div className="font-medium">{solicitud.hospedaje?.nombre || solicitud.nombrePropiedad}</div>
              </div>
            </div>
          </div>

          {/* Tabla de Items */}
          {isLoadingStock ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-2">Cargando stock disponible...</span>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Insumo</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Solicitado</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Stock</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">A Alistar</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {items.map((item, index) => (
                    <tr key={index} className={`hover:bg-gray-50 ${!item.tiene_stock ? 'bg-red-50' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{item.nombre}</div>
                        <Badge variant="outline" className="text-xs mt-1">
                          {item.tipo}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge className="bg-gray-100 text-gray-800 font-semibold">
                          {item.cantidad}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge className={item.tiene_stock ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {item.stock_disponible}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Input
                          type="number"
                          min="0"
                          max={Math.min(item.cantidad, item.stock_disponible)}
                          value={item.cantidad_alistar}
                          onChange={(e) => handleCantidadChange(index, e.target.value)}
                          className="w-20 text-center mx-auto"
                          disabled={item.stock_disponible === 0}
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        {item.tiene_stock ? (
                          <CheckCircle2 className="h-5 w-5 text-green-600 mx-auto" />
                        ) : (
                          <AlertCircle className="h-5 w-5 text-red-600 mx-auto" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Resumen */}
          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
            <div>
              <div className="text-sm text-gray-600">Total a Alistar</div>
              <div className="text-2xl font-bold text-blue-600">{totalAlistar} items</div>
            </div>
            {!todosConStock && (
              <div className="flex items-center gap-2 text-amber-600">
                <AlertCircle className="h-5 w-5" />
                <span className="text-sm font-medium">Algunos items no tienen stock completo</span>
              </div>
            )}
          </div>

          {/* Observaciones */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Observaciones (opcional)
            </label>
            <Textarea
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              placeholder="Agregar observaciones sobre el alistamiento..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleAlistar} 
            disabled={isLoading || isLoadingStock || totalAlistar === 0}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Alistando...
              </>
            ) : (
              <>
                <Package className="h-4 w-4 mr-2" />
                Alistar Entrega
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AlistarEntregaModal;
